import {
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  BarChartOutlined,
  BellOutlined,
  CalendarOutlined,
  ContactsOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  GlobalOutlined,
  HomeOutlined,
  SecurityScanOutlined,
  SettingOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
  TeamOutlined,
  ToolOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ComponentType } from 'react';

export interface RouteConfig {
  key: string;
  path: string;
  title: string;
  icon?: ComponentType;
  component?: string;
  breadcrumb?: string[];
  parent?: string;
  children?: RouteConfig[];
  permissions?: string[];
  isPublic?: boolean;
  hideInMenu?: boolean;
  exact?: boolean;
  meta?: {
    description?: string;
    keywords?: string[];
    requiresAuth?: boolean;
    layout?: 'default' | 'fullscreen' | 'minimal';
  };
}

export const ROUTE_PATHS = {
  // Auth routes
  LOGIN: '/login',
  SIGN_IN: '/sign-in',
  FORGOT_PASSWORD: '/forgot-password',

  // Main application routes
  ROOT: '/',
  DASHBOARD: '/dashboard',

  // User Management
  USERS: '/users',
  USERS_LIST: '/users/list',
  USERS_CREATE: '/users/create',
  USERS_EDIT: '/users/:id/edit',
  USERS_VIEW: '/users/:id',
  ROLES: '/users/roles',
  PERMISSIONS: '/users/permissions',

  // Inventory Management
  INVENTORY: '/inventory',
  INVENTORY_ITEMS: '/inventory/items',
  INVENTORY_CATEGORIES: '/inventory/categories',
  INVENTORY_SUPPLIERS: '/inventory/suppliers',
  INVENTORY_WAREHOUSES: '/inventory/warehouses',
  INVENTORY_MOVEMENTS: '/inventory/movements',

  // Master Data Management
  MASTER: '/master',
  MASTER_UOM: '/master/uom',
  MASTER_UOM_CATEGORIES: '/master/uom-categories',
  MASTER_UOM_CONVERSIONS: '/master/uom-conversions',
  MASTER_ITEM_GROUPS: '/master/item-groups',
  MASTER_ITEMS: '/master/items',
  MASTER_WAREHOUSES: '/master/warehouses',

  // Sales & Orders
  SALES: '/sales',
  ORDERS: '/sales/orders',
  ORDERS_CREATE: '/sales/orders/create',
  ORDERS_EDIT: '/sales/orders/:id/edit',
  CUSTOMERS: '/sales/customers',
  INVOICES: '/sales/invoices',
  QUOTATIONS: '/sales/quotations',

  // Purchases
  PURCHASES: '/purchases',
  PURCHASE_ORDERS: '/purchases/orders',
  VENDORS: '/purchases/vendors',
  PURCHASE_INVOICES: '/purchases/invoices',

  // Financial Management
  FINANCE: '/finance',
  ACCOUNTS: '/finance/accounts',
  TRANSACTIONS: '/finance/transactions',
  BUDGETS: '/finance/budgets',
  REPORTS_FINANCIAL: '/finance/reports',

  // Reports & Analytics
  REPORTS: '/reports',
  REPORTS_SALES: '/reports/sales',
  REPORTS_INVENTORY: '/reports/inventory',
  REPORTS_FINANCIAL_MAIN: '/reports/financial',
  REPORTS_CUSTOM: '/reports/custom',

  // Human Resources
  HR: '/hr',
  EMPLOYEES: '/hr/employees',
  PAYROLL: '/hr/payroll',
  ATTENDANCE: '/hr/attendance',
  LEAVE_MANAGEMENT: '/hr/leave',

  // System Management
  SETTINGS: '/settings',
  SETTINGS_GENERAL: '/settings/general',
  SETTINGS_COMPANY: '/settings/company',
  SETTINGS_SYSTEM: '/settings/system',
  SETTINGS_INTEGRATIONS: '/settings/integrations',
  SETTINGS_BACKUP: '/settings/backup',
  SETTINGS_SECURITY: '/settings/security',

  // Tools & Utilities
  TOOLS: '/tools',
  TOOLS_CALCULATOR: '/tools/calculator',
  TOOLS_CALENDAR: '/tools/calendar',
  TOOLS_NOTIFICATIONS: '/tools/notifications',
  TOOLS_AUDIT: '/tools/audit',

  // Profile & Account
  PROFILE: '/profile',
  ACCOUNT_SETTINGS: '/account',

  // Organizations
  ORGANIZATIONS: '/organizations',

  // Organization invitations
  ACCEPT_INVITATION: '/accept-invitation',

  // Error pages
  NOT_FOUND: '/404',
  UNAUTHORIZED: '/401',
  SERVER_ERROR: '/500',
} as const;

/**
 * @deprecated ROUTE_TREE is legacy and no longer used in the simplified router.
 *
 * The new routing architecture (AppRouter.tsx + routeConfig.ts) uses:
 * - Backend menu as single source of truth for routes
 * - Simple path-to-component mapping in routeConfig.ts
 * - Dynamic route generation from backend menu
 *
 * ROUTE_TREE duplicated 80% of backend menu data and is no longer needed.
 *
 * For new routes:
 * 1. Add menu item to backend menu.service.ts
 * 2. Add path → component mapping in src/router/routeConfig.ts
 *
 * This export is kept temporarily for backward compatibility with:
 * - Components that haven't been migrated yet
 *
 * TODO: Remove this entire ROUTE_TREE after confirming all routes work correctly
 * with the new simplified router architecture.
 */
export const ROUTE_TREE: RouteConfig[] = [
  {
    key: 'dashboard',
    path: ROUTE_PATHS.DASHBOARD,
    title: 'Dashboard',
    icon: DashboardOutlined,
    component: 'Dashboard',
    breadcrumb: ['Dashboard'],
    meta: {
      description: 'Main dashboard with overview and analytics',
      keywords: ['dashboard', 'overview', 'analytics'],
      requiresAuth: true,
    },
  },
  {
    key: 'inventory',
    path: ROUTE_PATHS.INVENTORY,
    title: 'Inventory',
    icon: ShopOutlined,
    breadcrumb: ['Inventory'],
    meta: {
      description: 'Manage inventory items and categories',
      requiresAuth: true,
    },
    children: [
      {
        key: 'inventory-items',
        path: ROUTE_PATHS.INVENTORY_ITEMS,
        title: 'Items',
        component: 'InventoryItems',
        parent: 'inventory',
        breadcrumb: ['Inventory', 'Items'],
        permissions: ['inventory.read'],
      },
      {
        key: 'inventory-categories',
        path: ROUTE_PATHS.INVENTORY_CATEGORIES,
        title: 'Item Groups',
        component: 'InventoryCategories',
        parent: 'inventory',
        breadcrumb: ['Inventory', 'Item Groups'],
        permissions: ['inventory.categories.read'],
      },
    ],
  },
  {
    key: 'master',
    path: ROUTE_PATHS.MASTER,
    title: 'Master Data',
    icon: DatabaseOutlined,
    breadcrumb: ['Master Data'],
    meta: {
      description: 'Manage master data for ERP system',
      requiresAuth: true,
    },
    children: [
      {
        key: 'master-items',
        path: ROUTE_PATHS.MASTER_ITEMS,
        title: 'Items',
        icon: AppstoreOutlined,
        component: 'ItemPage',
        parent: 'master',
        breadcrumb: ['Master Data', 'Items'],
        // permissions: ['master.items.read'],
      },
      {
        key: 'master-item-groups',
        path: ROUTE_PATHS.MASTER_ITEM_GROUPS,
        title: 'Item Groups',
        icon: TagsOutlined,
        component: 'ItemGroupPage',
        parent: 'master',
        breadcrumb: ['Master Data', 'Item Groups'],
        // permissions: ['master.item-groups.read'],
      },
      {
        key: 'master-warehouses',
        path: ROUTE_PATHS.MASTER_WAREHOUSES,
        title: 'Warehouses',
        icon: HomeOutlined,
        component: 'WarehousePage',
        parent: 'master',
        breadcrumb: ['Master Data', 'Warehouses'],
        // permissions: ['master.warehouses.read'],
      },
      {
        key: 'master-uom',
        path: ROUTE_PATHS.MASTER_UOM,
        title: 'Units of Measure',
        icon: ToolOutlined,
        component: 'UomPage',
        parent: 'master',
        breadcrumb: ['Master Data', 'Units of Measure'],
        // permissions: ['master.uom.read'],
      },
      {
        key: 'master-uom-categories',
        path: ROUTE_PATHS.MASTER_UOM_CATEGORIES,
        title: 'UOM Categories',
        icon: TagsOutlined,
        component: 'UomCategoryPage',
        parent: 'master',
        breadcrumb: ['Master Data', 'UOM Categories'],
        // permissions: ['master.uom.read'],
      },
      {
        key: 'master-uom-conversions',
        path: ROUTE_PATHS.MASTER_UOM_CONVERSIONS,
        title: 'UOM Conversions',
        icon: GlobalOutlined,
        component: 'UomConversionPage',
        parent: 'master',
        breadcrumb: ['Master Data', 'UOM Conversions'],
        // permissions: ['master.uom.read'],
      },
    ],
  },
];

// Helper functions for route management
export const findRouteByPath = (
  path: string,
  routes: RouteConfig[] = ROUTE_TREE,
): RouteConfig | null => {
  for (const route of routes) {
    if (route.path === path) {
      return route;
    }
    if (route.children) {
      const found = findRouteByPath(path, route.children);
      if (found) return found;
    }
  }
  return null;
};

export const findRouteByKey = (
  key: string,
  routes: RouteConfig[] = ROUTE_TREE,
): RouteConfig | null => {
  for (const route of routes) {
    if (route.key === key) {
      return route;
    }
    if (route.children) {
      const found = findRouteByKey(key, route.children);
      if (found) return found;
    }
  }
  return null;
};

export const getRouteHierarchy = (routeKey: string): RouteConfig[] => {
  const hierarchy: RouteConfig[] = [];

  const findHierarchy = (
    key: string,
    routes: RouteConfig[] = ROUTE_TREE,
    parents: RouteConfig[] = [],
  ): boolean => {
    for (const route of routes) {
      const currentPath = [...parents, route];

      if (route.key === key) {
        hierarchy.push(...currentPath);
        return true;
      }

      if (route.children && findHierarchy(key, route.children, currentPath)) {
        return true;
      }
    }
    return false;
  };

  findHierarchy(routeKey);
  return hierarchy;
};

export const getMenuRoutes = (routes: RouteConfig[] = ROUTE_TREE): RouteConfig[] => {
  return routes.filter((route) => !route.hideInMenu);
};

export const getActiveRoute = (pathname: string): RouteConfig | null => {
  // Try exact match first
  const found = findRouteByPath(pathname);
  if (found) return found;

  // Try pattern matching for dynamic routes
  for (const route of getAllRoutes()) {
    if (route.path.includes(':')) {
      const pattern = route.path.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      if (regex.test(pathname)) {
        return route;
      }
    }
  }

  return null;
};

export const getAllRoutes = (routes: RouteConfig[] = ROUTE_TREE): RouteConfig[] => {
  const allRoutes: RouteConfig[] = [];

  const collectRoutes = (routeList: RouteConfig[]) => {
    for (const route of routeList) {
      allRoutes.push(route);
      if (route.children) {
        collectRoutes(route.children);
      }
    }
  };

  collectRoutes(routes);
  return allRoutes;
};

export const getRouteBreadcrumbs = (pathname: string): string[] => {
  const route = getActiveRoute(pathname);
  return route?.breadcrumb || [];
};

export const isRouteAuthorized = (route: RouteConfig, userPermissions: string[] = []): boolean => {
  if (!route.permissions || route.permissions.length === 0) {
    return true;
  }

  return route.permissions.some((permission) => userPermissions.includes(permission));
};
